/**
 * <VitalsRecordModal> — capture a vitals reading and score NEWS2 live.
 *
 * The clinician enters respiratory rate / SpO2 / temp / BP / HR / level of
 * consciousness for a patient on their roster. As they type, we compute the
 * NEWS2 *preview* client-side (mirrors the backend rules in
 * `routes/readmission_engine.py::_news2`) so the clinician sees the score
 * before they hit save. On save we POST /api/vitals/{patient_id} which
 * persists the reading and (server-side) re-runs NEWS2 + fires the
 * deterioration alert when the band is medium/high.
 */
import React, { useMemo, useState } from 'react';
import axios from '../../lib/api';
import { X, Activity } from 'lucide-react';
import { toast } from 'sonner';
import useModalClose, { CLOSE_BTN_STYLE } from '../../hooks/useModalClose';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Client-side mirror of backend NEWS2 scoring (Scale 1 SpO2) — keep in sync
// with `routes/readmission_engine.py::_news2`.
const news2 = (v) => {
  const pts = {};
  const x = (n, fn) => (n == null || n === '' || Number.isNaN(Number(n)) ? 0 : fn(Number(n)));
  pts.rr = x(v.respiratory_rate, (n) => (n <= 8 ? 3 : n <= 11 ? 1 : n <= 20 ? 0 : n <= 24 ? 2 : 3));
  pts.spo2 = x(v.spo2, (n) => (n <= 91 ? 3 : n <= 93 ? 2 : n <= 95 ? 1 : 0));
  pts.o2 = v.on_supplemental_o2 ? 2 : 0;
  pts.temp = x(v.temperature_c, (n) => (n <= 35 ? 3 : n <= 36 ? 1 : n <= 38 ? 0 : n <= 39 ? 1 : 2));
  pts.sbp = x(v.systolic_bp, (n) => (n <= 90 ? 3 : n <= 100 ? 2 : n <= 110 ? 1 : n <= 219 ? 0 : 3));
  pts.hr = x(v.heart_rate, (n) => (n <= 40 ? 3 : n <= 50 ? 1 : n <= 90 ? 0 : n <= 110 ? 1 : n <= 130 ? 2 : 3));
  pts.acvpu = (v.consciousness || 'A').toUpperCase() === 'A' ? 0 : 3;
  const total = Object.values(pts).reduce((a, b) => a + b, 0);
  const anyThree = Object.values(pts).some((p) => p === 3);
  let band = 'none';
  if (total >= 7) band = 'high';
  else if (total >= 5 || anyThree) band = 'medium';
  else if (total >= 1) band = 'low';
  return { total, band, anyThree };
};

const BAND_COLOR = {
  none: 'border-cn-border bg-cn-bg/60 text-cn-text-muted',
  low: 'border-cn-success/40 bg-cn-success/8 text-cn-success',
  medium: 'border-cn-accent/40 bg-cn-accent/10 text-cn-accent',
  high: 'border-red-500/40 bg-red-500/10 text-red-600',
};

export const VitalsRecordModal = ({ open, patient, onClose, onSaved, getAuthHeader }) => {
  const { backdropProps, cardProps } = useModalClose({ open, onClose });
  const [form, setForm] = useState({
    respiratory_rate: '', spo2: '', on_supplemental_o2: false,
    temperature_c: '', systolic_bp: '', heart_rate: '', consciousness: 'A',
  });
  const [submitting, setSubmitting] = useState(false);
  const preview = useMemo(() => news2(form), [form]);

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSubmitting(true);
    const payload = {
      respiratory_rate: form.respiratory_rate ? Number(form.respiratory_rate) : null,
      spo2: form.spo2 ? Number(form.spo2) : null,
      on_supplemental_o2: !!form.on_supplemental_o2,
      temperature_c: form.temperature_c ? Number(form.temperature_c) : null,
      systolic_bp: form.systolic_bp ? Number(form.systolic_bp) : null,
      heart_rate: form.heart_rate ? Number(form.heart_rate) : null,
      consciousness: form.consciousness || 'A',
    };
    try {
      await axios.post(`${API}/vitals/${patient.patient_id}`, payload, getAuthHeader?.());
      toast.success(`Vitals saved — NEWS2 ${preview.total} (${preview.band})`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save vitals.');
    } finally {
      setSubmitting(false);
    }
  };

  const fields = [
    { key: 'respiratory_rate', label: 'Resp rate', suffix: '/min', step: 1 },
    { key: 'spo2', label: 'SpO₂', suffix: '%', step: 1 },
    { key: 'temperature_c', label: 'Temp', suffix: '°C', step: 0.1 },
    { key: 'systolic_bp', label: 'Systolic BP', suffix: 'mmHg', step: 1 },
    { key: 'heart_rate', label: 'Heart rate', suffix: 'bpm', step: 1 },
  ];

  return (
    <div {...backdropProps} className="fixed inset-0 z-[1000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="vitals-modal">
      <div {...cardProps} className="bg-white dark:bg-cn-ink-900 rounded-3xl border border-cn-border/60 shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-cn-border/60 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-cn-text-muted font-bold">Record vitals · NEWS2</p>
            <h2 className="text-lg font-display font-extrabold text-cn-text dark:text-white">
              {patient?.patient_name || `Patient ${patient?.patient_id?.slice(0, 8)}`}
            </h2>
          </div>
          <button onClick={onClose} className="touch-manipulation rounded-full hover:bg-cn-bg"
                  style={CLOSE_BTN_STYLE} aria-label="Close vitals modal"
                  data-testid="vitals-modal-close">
            <X className="w-5 h-5 text-cn-text-muted" />
          </button>
        </div>

        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            {fields.map((f) => (
              <label key={f.key} className="block">
                <span className="block text-[11px] font-bold uppercase tracking-wider text-cn-text-muted mb-1">{f.label}</span>
                <div className="relative">
                  <input
                    type="number"
                    inputMode="decimal"
                    step={f.step}
                    value={form[f.key]}
                    onChange={(e) => set(f.key, e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-cn-border bg-white dark:bg-cn-ink-800 text-cn-text dark:text-white text-sm pr-12"
                    data-testid={`vitals-input-${f.key}`}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-cn-text-muted font-bold">{f.suffix}</span>
                </div>
              </label>
            ))}

            <label className="block">
              <span className="block text-[11px] font-bold uppercase tracking-wider text-cn-text-muted mb-1">Consciousness (ACVPU)</span>
              <select
                value={form.consciousness}
                onChange={(e) => set('consciousness', e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-cn-border bg-white dark:bg-cn-ink-800 text-cn-text dark:text-white text-sm"
                data-testid="vitals-input-consciousness"
              >
                <option value="A">A — Alert</option>
                <option value="C">C — Confused</option>
                <option value="V">V — Voice</option>
                <option value="P">P — Pain</option>
                <option value="U">U — Unresponsive</option>
              </select>
            </label>
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={form.on_supplemental_o2}
              onChange={(e) => set('on_supplemental_o2', e.target.checked)}
              data-testid="vitals-input-supplemental_o2"
            />
            <span className="text-cn-text dark:text-white">On supplemental O₂</span>
          </label>

          <div className={`rounded-xl border-2 p-4 ${BAND_COLOR[preview.band]}`} data-testid="vitals-news2-preview">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Activity className="w-5 h-5" />
                <p className="text-xs font-bold uppercase tracking-wider">NEWS2 preview</p>
              </div>
              <p className="text-2xl font-display font-extrabold" data-testid="vitals-news2-total">{preview.total}</p>
            </div>
            <p className="text-[11px] mt-1 font-semibold uppercase" data-testid="vitals-news2-band">
              {preview.band === 'none' ? 'No score yet — enter vitals' : `${preview.band} clinical risk`}
              {preview.anyThree && preview.band !== 'high' && ' · single red-flag parameter'}
            </p>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-cn-border/60 flex justify-end gap-2">
          <button onClick={onClose} className="cn-btn cn-btn-outline !text-sm" disabled={submitting}>Cancel</button>
          <button onClick={submit} className="cn-btn cn-btn-primary !text-sm" disabled={submitting || preview.total === 0}
                  data-testid="vitals-submit">
            {submitting ? 'Saving…' : 'Save vitals'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VitalsRecordModal;
